import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { memo, useState } from 'react';
import DraggableItem from './components/DraggableItem';
import useSidebarData from './hooks/useSidebarData';

const SidebarContainer = memo(({ children, collapsed }) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--canvas-bg-elevated)',
      color: 'var(--canvas-text)',
      borderRight: '1px solid var(--canvas-border)',
      transition: 'padding 0.2s ease',
    }}
    data-collapsed={collapsed ? 'true' : 'false'}
  >
    {children}
  </Box>
));

const Sidebar = ({ collapsed = false, onToggleCollapsed, onAddPackage }) => {
  const [query, setQuery] = useState('');
  const { packages, isLoading, error } = useSidebarData();

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flex={1}
          minHeight={0}
        >
          <CircularProgress size={20} sx={{ color: 'var(--canvas-text)' }} />
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={collapsed ? 0.75 : 1.25}>
          <Alert severity="error" sx={{ fontSize: '0.72rem', px: collapsed ? 0.5 : 1 }}>
            {collapsed ? '!' : error}
          </Alert>
        </Box>
      );
    }

    const grouped = new Map();
    packages.filter(pkg => `${pkg.name} ${pkg.type}`.toLowerCase().includes(query.trim().toLowerCase())).forEach(pkg => {
      const family = (pkg.type || 'other').split('/')[0];
      if (!grouped.has(family)) grouped.set(family, []);
      grouped.get(family).push(pkg);
    });
    const groups = Array.from(grouped, ([family, items]) => ({family, items}));

    return (
      <List
        dense
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          py: 0.75,
          px: collapsed ? 0.5 : 0.75,
        }}
      >
        {groups.map((group) => (
          <Box key={group.family} component="li" sx={{ listStyle: 'none' }}>
            {!collapsed && (
              <Typography
                sx={{
                  color: 'var(--canvas-muted)',
                  fontSize: '0.62rem',
                  letterSpacing: '0.14em',
                  fontWeight: 700,
                  px: 0.5,
                  pt: 0.75,
                  pb: 0.25,
                }}
              >
                {group.family.toUpperCase()}
              </Typography>
            )}
            {group.items.map((pkg) => (
              <ListItem component="div" key={pkg.id || pkg.type} disableGutters sx={{ px: 0, py: 0.35 }}>
                <Tooltip
                  title={collapsed ? `${pkg.name} — click or drag to add` : `Click or drag to add ${pkg.name}`}
                  placement="right"
                >
                  <Box width="100%">
                    <DraggableItem packageData={pkg} collapsed={collapsed} onAdd={() => onAddPackage?.(pkg)} />
                  </Box>
                </Tooltip>
              </ListItem>
            ))}
          </Box>
        ))}
      </List>
    );
  };

  return (
    <SidebarContainer collapsed={collapsed}>
      <Box
        sx={{
          px: collapsed ? 0.35 : 0.5,
          py: 0.55,
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 0.25,
          minHeight: 36,
        }}
      >
        {!collapsed && (
          <Typography
            component="h2"
            sx={{
              color: 'var(--canvas-text)',
              fontWeight: 700,
              letterSpacing: '0.12em',
              fontSize: '0.68rem',
              lineHeight: 1.2,
              textTransform: 'uppercase',
              opacity: 0.92,
              pl: 0.5,
              flex: 1,
              minWidth: 0,
            }}
          >
            Block library
          </Typography>
        )}
        <Tooltip
          title={collapsed ? 'Expand packages' : 'Collapse packages'}
          placement="right"
        >
          <IconButton
            size="small"
            aria-label={collapsed ? 'Expand packages sidebar' : 'Collapse packages sidebar'}
            aria-expanded={!collapsed}
            onClick={onToggleCollapsed}
            sx={{
              color: 'var(--canvas-muted)',
              p: 0.35,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.14)',
              },
            }}
          >
            {collapsed ? (
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            ) : (
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
      {!collapsed && (
        <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
          <TextField size="small" fullWidth placeholder="Find a block…"
            value={query} onChange={(event) => setQuery(event.target.value)}
            inputProps={{ 'aria-label': 'Find package' }}
            sx={{ '& input': { fontSize: '0.75rem', p: 1 } }} />
          {query && !packages.some((pkg) => `${pkg.name} ${pkg.type}`.toLowerCase().includes(query.toLowerCase())) && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', py: 2 }}>No matching packages.</Typography>
          )}
        </Box>
      )}
      {renderContent()}
    </SidebarContainer>
  );
};

export default Sidebar;
